import React, { useEffect, useRef } from 'react'

import { AGGridClientSide, AGGridServerSide } from '@fsg/gui-bits'

import { AGGridWrapperProps } from '@gui/types'

// * AG Grid modules requires separate imports and module registration for the different row model types...
// * Which is why this wrapper became a proxy for serving the appropriate version based solely on rowModelType param

export function AGGridWrapper<ContextType>(props: AGGridWrapperProps) {
  const gridRef = useRef<any>(null);
  const onGridReady = (params: any) => {
    gridRef.current = params;
    props.onGridReady?.(params);
  }  

  const findColumn = (api: any, columnApi: any, identifier: string) => {
    const allColumns = columnApi.getAllGridColumns();
    return allColumns.find((col: any) => {
      const colDef = col.getColDef();
      return colDef.field?.toLowerCase().includes(identifier) || 
             colDef.headerName?.toLowerCase().includes(identifier);
    });
  };

  const triggerCellAction = (cell: HTMLElement) => {
    const actionableElement = cell.querySelector('a, button, [role="button"]');
    if (actionableElement) {
      actionableElement.dispatchEvent(new MouseEvent('click', {
        bubbles: true, cancelable: true, view: window
      }));
      return true;
    }
    
    const notesElement = cell.querySelector('div[title="View/Edit Notes"]');
    if (notesElement) {
      notesElement.dispatchEvent(new MouseEvent('click', {
        bubbles: true, cancelable: true, view: window
      }));
      return true;
    }
    
    return false;
  };

  const onCellKeyDown = (e: any) => {
    const api = e.api;
    const columnApi = e.columnApi;
    const key = e.event.key.toLowerCase();
    const rowIndex = e.rowIndex;
    
    if (!['p', 'e', 'd', 'n'].includes(key)) {
      return;
    }
    
    if (e.event.target.tagName === 'INPUT' || 
        e.event.target.tagName === 'TEXTAREA' || 
        e.event.target.tagName === 'SELECT' || 
        (e.event.target && 'isContentEditable' in e.event.target && e.event.target.isContentEditable) || 
        api.getEditingCells().length > 0) {
      return;
    }
    
    let targetColumn;
    
    if (key === 'p') {
      targetColumn = findColumn(api, columnApi, 'phone');
    } else if (key === 'e') {
      targetColumn = findColumn(api, columnApi, 'email');
    } else if (key === 'd') {
      targetColumn = findColumn(api, columnApi, 'follow') || findColumn(api, columnApi, 'date');
    } else if (key === 'n') {
      targetColumn = findColumn(api, columnApi, 'notes');
    }
    
    if (!targetColumn) return;
    
    e.event.preventDefault();
    
    api.setFocusedCell(rowIndex, targetColumn);
    api.ensureColumnVisible(targetColumn);
    
    if (key === 'd') {
      setTimeout(() => {
        const focusedCell = api.getFocusedCell();
        if (!focusedCell) return;
        
        api.__allowDateEditing = true;
        
        api.startEditingCell({
          rowIndex: focusedCell.rowIndex,
          colKey: focusedCell.column.getColId(),
          keyPress: 13
        });     
      
        setTimeout(() => { api.__allowDateEditing = false; }, 100);
      }, 50);
    } else if (key === 'p' || key === 'e' || key === 'n') {
      setTimeout(() => {
        const focusedCell = api.getFocusedCell();
        if (!focusedCell) return;
        
        const cellElement = document.querySelector(
          `.ag-row[row-index="${focusedCell.rowIndex}"] .ag-cell[col-id="${focusedCell.column.getId()}"]`
        ) as HTMLElement;
        
        if (cellElement) {
          triggerCellAction(cellElement);
        }
      }, 50);
    }
  };

  useEffect(() => {
    const focusGrid = () => {
      try {
        if (gridRef.current?.api) {
          const api = gridRef.current.api;
          const columns = gridRef.current.columnApi.getAllDisplayedColumns();
          
          if (columns?.length > 0) {
            if (api.getDisplayedRowCount() > 0) {
              api.deselectAll();
              api.setFocusedCell(0, columns[0]);
              api.ensureIndexVisible(0);
              return true;
            }
          }
        }
        
        const headerCell = document.querySelector('.ag-header-cell.ag-column-first') as HTMLElement;
        if (headerCell) {
          headerCell.tabIndex = -1;
          headerCell.focus();
          headerCell.style.outline = '2px solid #3b82f6';
          setTimeout(() => headerCell.style.outline = '', 1000);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error focusing grid:', error);
        return false;
      }
    };

    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const isShortcutPressed = (isMac && e.metaKey) || (!isMac && e.ctrlKey);
      
      if (isShortcutPressed && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        focusGrid();
        return;
      }
      
      // Only handle other shortcuts if we're not in an input field
      const activeElement = document.activeElement;
      const isInInput = activeElement instanceof HTMLInputElement || 
                       activeElement instanceof HTMLTextAreaElement || 
                       activeElement instanceof HTMLSelectElement || 
                       (activeElement && 'isContentEditable' in activeElement && activeElement.isContentEditable);
      
      if (isInInput) return;
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const enhancedProps = {
    ...props,
    onGridReady: onGridReady,
    onCellKeyDown: onCellKeyDown
  };

  switch (props.rowModelType) {
    case 'serverSide':
      return <AGGridServerSide<ContextType> {...enhancedProps} />
    // * May eventually need to expand to utilize the other two row mode types...
    // case "infinite":
    //   return <AGGridInfinite {...enhancedProps} />
    // case "viewport":
    //   return <AGGridViewport {...enhancedProps} />
    default:
      // * Default to client side
      return <AGGridClientSide<ContextType> {...enhancedProps} />
  }
}
